import React, { useState } from 'react';
import { useAuth } from '@/lib/auth/AuthContext';
import { exchangeWorkOSToken } from '@/lib/api/auth';
import { API_BASE_URL } from '@/config/api';
import { useUser } from '@/lib/contexts/UserContext';

export function AuthDebugger() {
  const { user, isAuthenticated, loading, login, logout } = useAuth();
  const { user: fullUser, updateUser } = useUser();
  const [manualToken, setManualToken] = useState('');
  const [testResult, setTestResult] = useState<{success: boolean, message: string} | null>(null);
  const [newName, setNewName] = useState('');

  const testDirectAuth = async () => {
    try {
      setTestResult(null);
      if (!manualToken) {
        setTestResult({success: false, message: 'Please enter a token to test'});
        return;
      }

      if (import.meta.env.DEV) {
        console.log('[AuthDebugger] Testing manual token:', {
          tokenLength: manualToken.length,
          tokenPrefix: manualToken.substring(0, 10) + '...'
        });
      }

      // Test direct fetch
      const response = await fetch(`${API_BASE_URL}/api/auth/workos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ token: manualToken }),
        credentials: 'include'
      });

      if (import.meta.env.DEV) {
        console.log('[AuthDebugger] Direct API response:', {
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries([...response.headers.entries()])
        });
      }

      if (response.ok) {
        const data = await response.json();
        if (import.meta.env.DEV) {
          console.log('[AuthDebugger] API response data:', data);
        }
        setTestResult({
          success: true,
          message: `Success! Token exchange worked. Response: ${JSON.stringify(data)}`
        });
      } else {
        const errorText = await response.text();
        console.error('[AuthDebugger] API error:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        });
        setTestResult({
          success: false,
          message: `API error: ${response.status} ${response.statusText}. Body: ${errorText}`
        });
      }
    } catch (error) {
      console.error('[AuthDebugger] Test error:', error);
      setTestResult({
        success: false,
        message: `Error: ${error instanceof Error ? error.message : String(error)}`
      });
    }
  };

  const testExchangeToken = async () => {
    try {
      setTestResult(null);
      if (!manualToken) {
        setTestResult({success: false, message: 'Please enter a token to test'});
        return;
      }

      if (import.meta.env.DEV) {
        console.log('[AuthDebugger] Testing token exchange with:', {
          tokenLength: manualToken.length,
          tokenPrefix: manualToken.substring(0, 10) + '...'
        });
      }

      const result = await exchangeWorkOSToken(manualToken);
      if (import.meta.env.DEV) {
        console.log('[AuthDebugger] Exchange result:', result);
      }
      
      setTestResult({
        success: true,
        message: `Success! Token exchange worked. JWT token length: ${result.token.length}`
      });
    } catch (error) {
      console.error('[AuthDebugger] Exchange error:', error);
      setTestResult({
        success: false,
        message: `Error: ${error instanceof Error ? error.message : String(error)}`
      });
    }
  };

  const updateUserName = async () => {
    try {
      if (!newName.trim()) {
        setTestResult({success: false, message: 'Please enter a name'});
        return;
      }
      
      await updateUser({ name: newName.trim() });
      setTestResult({success: true, message: `Name updated to: ${newName.trim()}`});
      setNewName('');
    } catch (error) {
      setTestResult({
        success: false,
        message: `Error updating name: ${error instanceof Error ? error.message : String(error)}`
      });
    }
  };

  if (!import.meta.env.DEV) {
    return null; // Only show in development
  }

  return (
    <div className="p-4 bg-orange-100 border border-orange-500 rounded-md mb-4">
      <h2 className="text-lg font-bold mb-2">Auth Debugger</h2>
      <div className="mb-4">
        <div className="mb-2">
          <span className="font-semibold">Status:</span>{' '}
          {loading ? 'Loading...' : isAuthenticated ? 'Authenticated' : 'Not authenticated'}
        </div>
        {user && (
          <div className="mb-2">
            <span className="font-semibold">User:</span>{' '}
            {user.email} ({user.id})
            <details className="mt-2">
              <summary className="cursor-pointer text-sm font-medium">Raw User Data</summary>
              <pre className="text-xs mt-1 p-2 bg-gray-100 rounded overflow-auto max-h-40">
                {JSON.stringify(user, null, 2)}
              </pre>
            </details>
          </div>
        )}
        {fullUser && (
          <div className="mb-2">
            <span className="font-semibold">Full User:</span>{' '}
            {fullUser.name} ({fullUser.email})
            <details className="mt-2">
              <summary className="cursor-pointer text-sm font-medium">Full User Data</summary>
              <pre className="text-xs mt-1 p-2 bg-gray-100 rounded overflow-auto max-h-40">
                {JSON.stringify(fullUser, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </div>
      
      <div className="mb-4">
        <button 
          className="px-3 py-1 bg-blue-500 text-white rounded mr-2" 
          onClick={login}
        >
          Login
        </button>
        <button 
          className="px-3 py-1 bg-red-500 text-white rounded" 
          onClick={logout}
        >
          Logout
        </button>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">
          Update Display Name:
        </label>
        <div className="flex space-x-2">
          <input 
            type="text" 
            value={newName} 
            onChange={(e) => setNewName(e.target.value)}
            className="flex-1 p-2 border rounded"
            placeholder="Enter new display name"
          />
          <button 
            className="px-3 py-1 bg-green-500 text-white rounded" 
            onClick={updateUserName}
          >
            Update Name
          </button>
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-1">
          Test with WorkOS token:
        </label>
        <input 
          type="text" 
          value={manualToken} 
          onChange={(e) => setManualToken(e.target.value)}
          className="w-full p-2 border rounded mb-2"
          placeholder="Paste WorkOS token here"
        />
        <div className="flex space-x-2">
          <button 
            className="px-3 py-1 bg-green-500 text-white rounded" 
            onClick={testDirectAuth}
          >
            Test Direct API
          </button>
          <button 
            className="px-3 py-1 bg-purple-500 text-white rounded" 
            onClick={testExchangeToken}
          >
            Test Token Exchange
          </button>
        </div>
      </div>

      {testResult && (
        <div className={`p-3 rounded ${testResult.success ? 'bg-green-100 border border-green-500' : 'bg-red-100 border border-red-500'}`}>
          <div className="font-semibold">{testResult.success ? 'Success' : 'Error'}</div>
          <div className="text-sm">{testResult.message}</div>
        </div>
      )}

      <div className="mt-4 text-xs text-gray-500">
        <div>Configured API URL: {import.meta.env.VITE_API_URL}</div>
        <div>Current API URL: {API_BASE_URL}</div>
        <div>Auth Callback: {import.meta.env.VITE_AUTH_CALLBACK_URL}</div>
        <div>WorkOS Client ID: {import.meta.env.VITE_WORKOS_CLIENT_ID ? '✓ Set' : '❌ Missing'}</div>
      </div>
    </div>
  );
} 